/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from "react";
import {
  useMerchantAccessByIdData,
  useMerchantAccessCenterListData,
} from "../../../apis/merchantApis";
import { UseFormSetValue } from "react-hook-form";
import { T, useLanguage } from "../../../context/LanguageContext";

type ActionType = "create" | "read" | "update" | "delete";

type PermissionItem = {
  id: string;
  name: string;
  key: string;
  actions: Record<ActionType, boolean>;
};

type PermissionMatrixProps = {
  register: any;
  errors: any;
  setValue?: UseFormSetValue<any>;
  defaultPermissions?: { module: string; operations: ActionType[] }[];
  readOnly?: boolean;
  clientId?: string;
};

const ClientPermissionMatrix: React.FC<PermissionMatrixProps> = ({
  register,
  errors,
  setValue,
  defaultPermissions = [],
  readOnly = false,
  clientId = "",
}) => {
  const { t } = useLanguage();
  const { data: existingPermissions } = useMerchantAccessByIdData(clientId);
  const { data: allAccessCenters } = useMerchantAccessCenterListData();
  const [permissions, setPermissions] = useState<PermissionItem[]>([]);
  const selectAllRefs = useRef<Array<HTMLInputElement | null>>([]);
  const globalSelectAllRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (allAccessCenters && allAccessCenters.length > 0) {
      const clientPermMap = new Map<string, string[]>();
      if (existingPermissions && Array.isArray(existingPermissions)) {
        existingPermissions.forEach((item: any) => {
          clientPermMap.set(item.module, item.permissions || []);
        });
      }

      const initialized = allAccessCenters.map((item: any) => {
        const existingPerms = clientPermMap.get(item.key) || [];
        return {
          id: item.id,
          name: item.name,
          key: item.key,
          actions: {
            create: existingPerms.includes("create"),
            read: existingPerms.includes("read"),
            update: existingPerms.includes("update"),
            delete: existingPerms.includes("delete"),
          },
        };
      });
      setPermissions(initialized);
    }
  }, [allAccessCenters, existingPermissions]);

  useEffect(() => {
    if (permissions.length === 0 || defaultPermissions.length === 0) return;

    const updated = permissions.map((perm) => {
      const matched = defaultPermissions.find(
        (dp) => dp.module === perm.key || dp.module === perm.name,
      );
      if (matched) {
        const newActions = { ...perm.actions };
        matched.operations.forEach((op) => {
          if (op in newActions) newActions[op as ActionType] = true;
        });
        return { ...perm, actions: newActions };
      }
      return perm;
    });

    setPermissions(updated);
  }, [defaultPermissions, permissions.length]);

  useEffect(() => {
    permissions.forEach((section, idx) => {
      const ref = selectAllRefs.current[idx];
      if (!ref) return;

      const values = Object.values(section.actions);
      const allTrue = values.every(Boolean);
      const someTrue = values.some(Boolean);

      ref.checked = allTrue;
      ref.indeterminate = !allTrue && someTrue;
    });
  }, [permissions]);

  useEffect(() => {
    if (readOnly) return;
    const globalCheckbox = document.querySelector('input[name="globalSelectAll"]') as HTMLInputElement;
    if (!globalCheckbox) return;

    const allActions = permissions.flatMap((p) => Object.values(p.actions));
    const total = allActions.length;
    const checkedCount = allActions.filter(Boolean).length;

    const isFullyChecked = checkedCount === total && total > 0;
    const isPartiallyChecked = checkedCount > 0 && checkedCount < total;

    globalCheckbox.checked = isFullyChecked;
    globalCheckbox.indeterminate = isPartiallyChecked;
  }, [permissions, readOnly]);

  useEffect(() => {
    if (!setValue) return;

    const transformed = permissions
      .filter((p) => Object.values(p.actions).some(Boolean))
      .map((p) => ({
        module: p.key, // ✅ FIX
        operations: Object.entries(p.actions)
          .filter(([, val]) => val)
          .map(([key]) => key as ActionType),
      }));

    setValue("permissions", transformed, { shouldValidate: true });
  }, [permissions, setValue]);

  useEffect(() => {
    register("permissions", {
      validate: (value: any) =>
        !value || value.length === 0
          ? "At least one permission is required"
          : true,
    });
  }, [register]);

  useEffect(() => {
    if (readOnly) return;
    const globalCheckbox = document.querySelector('input[name="globalSelectAll"]') as HTMLInputElement;
    if (!globalCheckbox) return;

    const handleChange = (e: Event) => {
      const target = e.target as HTMLInputElement;
      const shouldSelect = target.checked;

      setPermissions((prev) =>
        prev.map((section) => ({
          ...section,
          actions: {
            create: shouldSelect,
            read: shouldSelect,
            update: shouldSelect,
            delete: shouldSelect,
          },
        })),
      );
    };

    globalCheckbox.addEventListener("change", handleChange);
    return () => globalCheckbox.removeEventListener("change", handleChange);
  }, [readOnly]);

  const togglePermission = (index: number, action: ActionType) => {
    if (readOnly) return;
    setPermissions((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        actions: {
          ...updated[index].actions,
          [action]: !updated[index].actions[action],
        },
      };
      return updated;
    });
  };

  const toggleSelectAll = (index: number, checked: boolean) => {
    if (readOnly) return;
    setPermissions((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        actions: {
          create: checked,
          read: checked,
          update: checked,
          delete: checked,
        },
      };
      return updated;
    });
  };

  return (
    <div className="divide-y divide-[#000000]/10">
      {permissions.map((section, idx) => (
        <div key={section.id} className="mb-6 py-3 px-6 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-1.5 cursor-pointer">
                <div className="relative flex items-center">
                  <input
                    type="checkbox"
                    ref={(el) => {
                      selectAllRefs.current[idx] = el;
                    }}
                    disabled={readOnly}
                    onChange={(e) => toggleSelectAll(idx, e.target.checked)}
                    className="peer h-5 w-5 cursor-pointer appearance-none rounded border border-slate-300 checked:border-[#FBD037] transition-all bg-white checked:bg-[#FBD037] checked:text-white indeterminate:bg-[#FBD037]/70 indeterminate:border-[#FBD037]/70"
                  />
                  <span className="absolute text-xs font-bold text-white peer-checked:opacity-100 opacity-0 transition-opacity duration-200 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none">
                    ✓
                  </span>
                </div>
                <span className="text-sm font-medium text-[#212121]">
                  <T>{section.name}</T>
                </span>
              </label>
            </div>

            <div className="grid grid-cols-4 gap-x-3">
              {(["create", "read", "update", "delete"] as ActionType[]).map(
                (action) => {
                  const isActive = section.actions[action];

                  if (readOnly) {
                    const colorMap = {
                      create: "bg-blue-100 text-blue-700",
                      read: "bg-green-100 text-green-700",
                      update: "bg-yellow-100 text-yellow-800",
                      delete: "bg-red-100 text-red-700",
                    };

                    return (
                      <span
                        key={action}
                        className={`px-3 py-1 rounded-full text-xs font-semibold tracking-wide border text-center ${
                          isActive
                            ? colorMap[action]
                            : "bg-gray-100 text-[#212121]/70"
                        }`}
                      >
                        {t(action).toUpperCase()}
                      </span>
                    );
                  }

                  return (
                    <label
                      key={action}
                      className="flex items-center gap-2 cursor-pointer"
                    >
                      <div className="relative flex items-center">
                        <input
                          type="checkbox"
                          disabled={readOnly}
                          checked={section.actions[action]}
                          onChange={() => togglePermission(idx, action)}
                          className="peer h-5 w-5 cursor-pointer appearance-none rounded border border-slate-300 checked:border-[#FBD037] transition-all bg-white checked:bg-[#FBD037]"
                        />
                        <span className="absolute text-xs font-bold text-white peer-checked:opacity-100 opacity-0 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none">
                          ✓
                        </span>
                      </div>
                      <span className="text-base text-[#212121] capitalize">
                        <T>{action}</T>
                      </span>
                    </label>
                  );
                },
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default ClientPermissionMatrix;
